import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminUsers.css";

/* webpack require.context for user photos */
let USER_PHOTOS = {};
try {
  const ctx = require.context(
    "../../assets/uploads/users",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  USER_PHOTOS = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) { /* fall back to API URL */ }

function userPhoto(filename) {
  if (!filename) return null;
  return USER_PHOTOS[filename] ?? `${API_BASE}/uploads/users/${filename}`;
}

/* Role config */
const ROLES = ["admin", "editor", "viewer", "staff"];

const ROLE_CONFIG = {
  admin:  { label: "Admin",  color: "#d62828", bg: "#fef2f2", border: "#fecaca" },
  editor: { label: "Editor", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  viewer: { label: "Viewer", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  staff:  { label: "Staff",  color: "#15803d", bg: "#e6f9f0", border: "#bbf7d0" },
};

const EMPTY_FORM = {
  username: "",
  email:    "",
  role:     "admin",
  active:   1,
  photo:    null,  // new File upload
};

const EMPTY_PW_FORM = {
  new_password:     "",
  confirm_password: "",
};

/* ─────────────────────────────────────
   Role Badge
   ───────────────────────────────────── */
function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || { label: role, color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" };
  return (
    <span
      className="auser-role-badge"
      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
    >
      {cfg.label}
    </span>
  );
}

/* ─────────────────────────────────────
   Status Pill
   ───────────────────────────────────── */
function StatusPill({ active }) {
  return (
    <span className={`auser-status-pill ${Number(active) === 1 ? "auser-status-pill--active" : "auser-status-pill--inactive"}`}>
      {Number(active) === 1 ? "● Active" : "○ Inactive"}
    </span>
  );
}

/* ─────────────────────────────────────
   Avatar
   ───────────────────────────────────── */
function UserAvatar({ photo, username, size = 36 }) {
  const src = userPhoto(photo);
  const initials = (username || "?").slice(0, 2).toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={username}
        className="auser-avatar"
        style={{ width: size, height: size }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  return (
    <div className="auser-avatar-initials" style={{ width: size, height: size, fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

/* ─────────────────────────────────────
   Dot Menu
   ───────────────────────────────────── */
function DotMenu({ onEdit, onResetPassword, onDelete, isSelf }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="auser-dot-menu" ref={ref}>
      <button
        className="auser-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="auser-dot-menu__dropdown">
          <button onClick={() => { onEdit(); setOpen(false); }}>
            <span className="auser-dot-menu__icon">✏</span> Edit
          </button>
          <button onClick={() => { onResetPassword(); setOpen(false); }}>
            <span className="auser-dot-menu__icon">🔑</span> Reset Password
          </button>
          {!isSelf && (
            <button className="auser-dot-menu__delete" onClick={() => { onDelete(); setOpen(false); }}>
              <span className="auser-dot-menu__icon">🗑</span> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   ADD / EDIT MODAL
   ═══════════════════════════════════ */
function UserModal({ item, onClose, onSaved, currentUserId }) {
  const isEdit  = !!item?.id;
  const isSelf  = isEdit && item.id === currentUserId;

  const [form,     setForm]     = useState(EMPTY_FORM);
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [preview,  setPreview]  = useState(null);
  const [clearPhoto, setClearPhoto] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [showCPw,  setShowCPw]  = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (item) {
      setForm({
        username: item.username || "",
        email:    item.email    || "",
        role:     item.role     || "admin",
        active:   Number(item.active) === 1 ? 1 : 0,
        photo:    null,
      });
      setPreview(item.photo ? userPhoto(item.photo) : null);
      setClearPhoto(false);
    } else {
      setForm(EMPTY_FORM);
      setPreview(null);
      setPassword("");
      setConfirm("");
      setClearPhoto(false);
    }
  }, [item]);

  const set = (key, val) => { setForm((f) => ({ ...f, [key]: val })); setError(""); };

  const handleFile = (file) => {
    if (!file) return;
    set("photo", file);
    setClearPhoto(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClearPhoto = () => {
    set("photo", null);
    setPreview(null);
    setClearPhoto(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async () => {
    setError("");
    if (!form.username.trim()) return setError("Username is required.");
    if (!isEdit && !password)  return setError("Password is required for new users.");
    if (!isEdit && password !== confirm) return setError("Passwords do not match.");
    if (!isEdit && password.length < 8)  return setError("Password must be at least 8 characters.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("username", form.username.trim());
      fd.append("email",    form.email    || "");
      fd.append("role",     form.role);
      fd.append("active",   form.active);
      if (!isEdit && password) fd.append("password", password);
      if (form.photo)   fd.append("photo", form.photo);
      if (clearPhoto)   fd.append("clear_photo", "1");

      if (isEdit) {
        await apiFetch(`/api/admin/users/${item.id}`, { method: "PUT", body: fd });
      } else {
        await apiFetch("/api/admin/users", { method: "POST", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="auser-modal-backdrop" onClick={handleBackdrop}>
      <div className="auser-modal">

        {/* Header */}
        <div className="auser-modal__header">
          <div className="auser-modal__title-row">
            <span className="auser-modal__tag">{isEdit ? "EDIT" : "NEW"}</span>
            <h2 className="auser-modal__title">
              {isEdit ? `Edit — ${item.username}` : "Add Admin User"}
            </h2>
          </div>
          <button className="auser-modal__close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="auser-modal__body">
          {error && <div className="auser-alert auser-alert--error">⚠ {error}</div>}

          {isSelf && (
            <div className="auser-alert auser-alert--info">
              ℹ You are editing your own account. Role and active status are locked.
            </div>
          )}

          {/* ── Account Details ── */}
          <div className="auser-msec">
            <h4 className="auser-msec__title">Account Details</h4>
            <div className="auser-two-col">
              <div className="auser-field">
                <label className="auser-field__label">
                  Username <span className="auser-req">*</span>
                </label>
                <input
                  className="auser-input"
                  value={form.username}
                  onChange={(e) => set("username", e.target.value)}
                  placeholder="johndoe"
                  autoComplete="off"
                />
              </div>
              <div className="auser-field">
                <label className="auser-field__label">Email Address</label>
                <input
                  className="auser-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="john@example.com"
                />
              </div>
              <div className="auser-field">
                <label className="auser-field__label">Role</label>
                <select
                  className="auser-input"
                  value={form.role}
                  onChange={(e) => set("role", e.target.value)}
                  disabled={isSelf}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_CONFIG[r]?.label || r}</option>
                  ))}
                </select>
              </div>
              <div className="auser-field">
                <label className="auser-field__label">Visibility</label>
                <label className="auser-check" style={{ marginTop: 22 }}>
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                    disabled={isSelf}
                  />
                  <span>Active (can log in)</span>
                </label>
              </div>
            </div>
          </div>

          {/* ── Password (create only) ── */}
          {!isEdit && (
            <div className="auser-msec">
              <h4 className="auser-msec__title">Password</h4>
              <div className="auser-two-col">
                <div className="auser-field">
                  <label className="auser-field__label">
                    Password <span className="auser-req">*</span>
                  </label>
                  <div className="auser-pw-wrap">
                    <input
                      className="auser-input"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(""); }}
                      placeholder="Min. 8 characters"
                      autoComplete="new-password"
                    />
                    <button type="button" className="auser-pw-eye" onClick={() => setShowPw((v) => !v)}>
                      {showPw ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>
                <div className="auser-field">
                  <label className="auser-field__label">
                    Confirm Password <span className="auser-req">*</span>
                  </label>
                  <div className="auser-pw-wrap">
                    <input
                      className="auser-input"
                      type={showCPw ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                      placeholder="Repeat password"
                      autoComplete="new-password"
                    />
                    <button type="button" className="auser-pw-eye" onClick={() => setShowCPw((v) => !v)}>
                      {showCPw ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>
              </div>
              <p className="auser-field__hint" style={{ marginTop: 4 }}>
                Use at least 8 characters with a mix of letters and numbers.
              </p>
            </div>
          )}

          {/* ── Profile Photo ── */}
          <div className="auser-msec">
            <h4 className="auser-msec__title">Profile Photo <span className="auser-msec__optional">(optional)</span></h4>
            <div className="auser-photo-wrap">
              <div
                className={`auser-photo-drop ${preview ? "auser-photo-drop--has-photo" : ""}`}
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="auser-photo-drop__preview" />
                ) : (
                  <div className="auser-photo-drop__placeholder">
                    <span className="auser-photo-drop__icon">👤</span>
                    <span className="auser-photo-drop__text">Click to upload photo</span>
                    <span className="auser-photo-drop__sub">PNG, JPG, WebP — max 10 MB</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              <div className="auser-photo-actions">
                <button type="button" className="auser-image-btn" onClick={() => fileRef.current?.click()}>
                  {preview ? "Change Photo" : "Upload Photo"}
                </button>
                {preview && (
                  <button type="button" className="auser-image-btn auser-image-btn--remove" onClick={handleClearPhoto}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="auser-modal__footer">
          <div />
          <div className="auser-modal__footer-right">
            <button className="auser-btn auser-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="auser-btn auser-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create User"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   RESET PASSWORD MODAL
   ═══════════════════════════════════ */
function ResetPasswordModal({ item, onClose, onSaved }) {
  const [form,    setForm]    = useState(EMPTY_PW_FORM);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [showPw,  setShowPw]  = useState(false);
  const [showCPw, setShowCPw] = useState(false);

  const set = (key, val) => { setForm((f) => ({ ...f, [key]: val })); setError(""); };

  const handleSave = async () => {
    setError("");
    if (!form.new_password)                          return setError("New password is required.");
    if (form.new_password.length < 8)                return setError("Password must be at least 8 characters.");
    if (form.new_password !== form.confirm_password) return setError("Passwords do not match.");

    setSaving(true);
    try {
      await apiFetch(`/api/admin/users/${item.id}/reset-password`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ new_password: form.new_password }),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="auser-modal-backdrop" onClick={handleBackdrop}>
      <div className="auser-modal auser-modal--narrow">

        <div className="auser-modal__header">
          <div className="auser-modal__title-row">
            <span className="auser-modal__tag">SECURITY</span>
            <h2 className="auser-modal__title">Reset Password</h2>
          </div>
          <button className="auser-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="auser-modal__body">
          <div className="auser-reset-user">
            <UserAvatar photo={item?.photo} username={item?.username} size={40} />
            <div>
              <p className="auser-reset-user__name">{item?.username}</p>
              <p className="auser-reset-user__email">{item?.email || "No email set"}</p>
            </div>
          </div>

          {error && <div className="auser-alert auser-alert--error">⚠ {error}</div>}

          <div className="auser-msec">
            <h4 className="auser-msec__title">New Password</h4>
            <div className="auser-two-col">
              <div className="auser-field">
                <label className="auser-field__label">
                  New Password <span className="auser-req">*</span>
                </label>
                <div className="auser-pw-wrap">
                  <input
                    className="auser-input"
                    type={showPw ? "text" : "password"}
                    value={form.new_password}
                    onChange={(e) => set("new_password", e.target.value)}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                  />
                  <button type="button" className="auser-pw-eye" onClick={() => setShowPw((v) => !v)}>
                    {showPw ? "🙈" : "👁"}
                  </button>
                </div>
              </div>
              <div className="auser-field">
                <label className="auser-field__label">
                  Confirm Password <span className="auser-req">*</span>
                </label>
                <div className="auser-pw-wrap">
                  <input
                    className="auser-input"
                    type={showCPw ? "text" : "password"}
                    value={form.confirm_password}
                    onChange={(e) => set("confirm_password", e.target.value)}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />
                  <button type="button" className="auser-pw-eye" onClick={() => setShowCPw((v) => !v)}>
                    {showCPw ? "🙈" : "👁"}
                  </button>
                </div>
              </div>
            </div>
            <p className="auser-field__hint" style={{ marginTop: 6 }}>
              This will immediately replace the user's current password.
            </p>
          </div>
        </div>

        <div className="auser-modal__footer">
          <div />
          <div className="auser-modal__footer-right">
            <button className="auser-btn auser-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="auser-btn auser-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Reset Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE CONFIRM DIALOG
   ═══════════════════════════════════ */
function DeleteDialog({ item, onCancel, onConfirm, deleting }) {
  return (
    <div
      className="auser-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="auser-confirm">
        <div className="auser-confirm__icon">🗑</div>
        <h3 className="auser-confirm__title">Delete User?</h3>
        <p className="auser-confirm__msg">
          This will permanently delete the account for{" "}
          <strong>{item?.username}</strong> and remove their profile photo.
          This action cannot be undone.
        </p>
        <div className="auser-confirm__actions">
          <button className="auser-btn auser-btn--cancel" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className="auser-btn auser-btn--danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminUsers() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  /* Current logged-in user id — prevent self-delete */
  const currentUserId = (() => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.id || null;
    } catch { return null; }
  })();

  /* Filters */
  const [search,      setSearch]      = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterRole,  setFilterRole]  = useState("");
  const [sort,        setSort]        = useState("newest");

  /* Modals */
  const [editTarget,   setEditTarget]   = useState(null);
  const [resetTarget,  setResetTarget]  = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Load ── */
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, limit: 200 });
    if (search)              params.set("search", search);
    if (filterActive !== "") params.set("active", filterActive);
    if (filterRole   !== "") params.set("role",   filterRole);

    apiFetch(`/api/admin/users?${params}`)
      .then((data) => setItems(data.users || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, filterActive, filterRole, sort]);

  useEffect(() => { load(); }, [load]);

  /* ── Stats ── */
  const totalActive   = items.filter((i) => Number(i.active) === 1).length;
  const totalInactive = items.filter((i) => Number(i.active) !== 1).length;
  const adminCount    = items.filter((i) => i.role === "admin").length;

  /* ── Toggle ── */
  const handleToggle = async (item) => {
    if (item.id === currentUserId) return setError("You cannot deactivate your own account.");
    setError(""); setSuccess("");
    try {
      const res = await apiFetch(`/api/admin/users/${item.id}/toggle`, { method: "PUT" });
      setSuccess(res.message || "Status updated.");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/users/${deleteTarget.id}`, { method: "DELETE" });
      setSuccess(`User "${deleteTarget.username}" deleted.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = (msg) => {
    setEditTarget(null);
    setResetTarget(null);
    setSuccess(msg || "Saved successfully.");
    load();
  };

  const formatDate = (dt) => {
    if (!dt) return "—";
    return new Date(dt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (loading) {
    return (
      <AdminLayout pageTag="SETTINGS" pageTitle="Admin Users">
        <div className="auser-skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTag="SETTINGS" pageTitle="Admin Users">

      {/* ── Intro bar ── */}
      <div className="auser-intro-bar">
        <div className="auser-intro-bar__left">
          <span className="auser-intro-bar__icon">👥</span>
          <div>
            <h2 className="auser-intro-bar__title">Admin Users</h2>
            <p className="auser-intro-bar__sub">
              Manage administrator accounts and access roles.
            </p>
          </div>
        </div>
        <div className="auser-intro-bar__right">
          <span className="auser-stat-pill">
            <span className="auser-stat-pill__num">{totalActive}</span> Active
          </span>
          <span className="auser-stat-pill">
            <span className="auser-stat-pill__num">{totalInactive}</span> Inactive
          </span>
          <span className="auser-stat-pill">
            <span className="auser-stat-pill__num">{adminCount}</span> Admins
          </span>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="auser-alert auser-alert--error">⚠ {error}</div>}
      {success && <div className="auser-alert auser-alert--success">✓ {success}</div>}

      {/* ── Toolbar ── */}
      <div className="auser-toolbar">
        <div className="auser-toolbar__left">
          <div className="auser-search-wrap">
            <span className="auser-search-icon">⌕</span>
            <input
              className="auser-search"
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="auser-filter-select"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <select
            className="auser-filter-select"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="">All Roles</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_CONFIG[r]?.label || r}</option>
            ))}
          </select>
          <select
            className="auser-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="username_asc">Username A-Z</option>
            <option value="last_login">Last Login</option>
          </select>
        </div>
        <button className="auser-btn-add" onClick={() => setEditTarget({})}>
          + Add User
        </button>
      </div>

      {/* ── Table ── */}
      {items.length === 0 ? (
        <div className="auser-empty">
          <div className="auser-empty__icon">👥</div>
          <h3 className="auser-empty__title">No Users Found</h3>
          <p className="auser-empty__sub">
            {search || filterActive !== "" || filterRole !== ""
              ? "Try adjusting your filters."
              : "Click \"Add User\" to create the first admin user."}
          </p>
        </div>
      ) : (
        <div className="auser-table-wrap">
          <table className="auser-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isSelf = item.id === currentUserId;
                return (
                  <tr
                    key={item.id}
                    className={Number(item.active) !== 1 ? "auser-row--inactive" : ""}
                  >
                    <td>
                      <div className="auser-user-cell">
                        <UserAvatar photo={item.photo} username={item.username} size={34} />
                        <div>
                          <span className="auser-user-cell__name">
                            {item.username}
                            {isSelf && <span className="auser-you-badge">You</span>}
                          </span>
                          <span className="auser-user-cell__id">ID #{item.id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="auser-email-cell">
                        {item.email || <span className="auser-muted">—</span>}
                      </span>
                    </td>
                    <td><RoleBadge role={item.role} /></td>
                    <td>
                      <span className="auser-date-cell">{formatDate(item.last_login)}</span>
                    </td>
                    <td>
                      <span className="auser-date-cell">{formatDate(item.created_at)}</span>
                    </td>
                    <td><StatusPill active={item.active} /></td>
                    <td>
                      <div className="auser-actions">
                        {!isSelf && (
                          <button
                            className={`auser-icon-btn ${
                              Number(item.active) === 1
                                ? "auser-icon-btn--toggle-on"
                                : "auser-icon-btn--toggle-off"
                            }`}
                            title={Number(item.active) === 1 ? "Deactivate" : "Activate"}
                            onClick={() => handleToggle(item)}
                          >
                            {Number(item.active) === 1 ? "✓" : "○"}
                          </button>
                        )}
                        <DotMenu
                          onEdit={() => setEditTarget(item)}
                          onResetPassword={() => setResetTarget(item)}
                          onDelete={() => setDeleteTarget(item)}
                          isSelf={isSelf}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit / Add Modal ── */}
      {editTarget !== null && (
        <UserModal
          item={Object.keys(editTarget).length === 0 ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => handleSaved("User saved successfully.")}
          currentUserId={currentUserId}
        />
      )}

      {/* ── Reset Password Modal ── */}
      {resetTarget && (
        <ResetPasswordModal
          item={resetTarget}
          onClose={() => setResetTarget(null)}
          onSaved={() => handleSaved("Password reset successfully.")}
        />
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <DeleteDialog
          item={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

    </AdminLayout>
  );
}